import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const UsersPermissions = ({ onChange }) => {
  const [users, setUsers] = useState([
    {
      id: 1,
      name: 'John Smith',
      email: 'john@accountingpro.com',
      role: 'admin',
      status: 'active',
      lastActive: '2026-01-30 10:30 AM'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah@accountingpro.com',
      role: 'accountant',
      status: 'active',
      lastActive: '2026-01-30 09:15 AM'
    },
    {
      id: 3,
      name: 'Mike Davis',
      email: 'mike@accountingpro.com',
      role: 'viewer',
      status: 'invited',
      lastActive: 'Never'
    }
  ]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'viewer' });

  const roleOptions = [
    { value: 'admin', label: 'Administrator - Full access' },
    { value: 'accountant', label: 'Accountant - Manage transactions' },
    { value: 'bookkeeper', label: 'Bookkeeper - Data entry only' },
    { value: 'viewer', label: 'Viewer - Read-only access' }
  ];

  const handleInvite = () => {
    console.log('Inviting user:', inviteData);
    setShowInviteModal(false);
    setInviteData({ email: '', role: 'viewer' });
    onChange?.();
  };

  const handleRoleChange = (userId, newRole) => {
    setUsers(users?.map(user => 
      user?.id === userId ? { ...user, role: newRole } : user
    ));
    onChange?.();
  };

  const handleRemoveUser = (userId) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      setUsers(users?.filter(user => user?.id !== userId));
      onChange?.();
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      accountant: 'bg-blue-100 text-blue-800',
      bookkeeper: 'bg-green-100 text-green-800',
      viewer: 'bg-gray-100 text-gray-800'
    };
    return colors?.[role] || colors?.viewer;
  };

  const getStatusBadgeColor = (status) => {
    return status === 'active' ?'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
          <p className="text-sm text-gray-600 mt-1">Manage user access and permissions</p>
        </div>
        <Button
          iconName="UserPlus"
          onClick={() => setShowInviteModal(true)}
        >
          Invite User
        </Button>
      </div>
      {/* Users Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Active
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.map((user) => (
              <tr key={user?.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                      <div className="text-sm text-gray-500">{user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user?.role)}`}>
                    {user?.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(user?.status)}`}>
                    {user?.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user?.lastActive}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => console.log('Edit user', user?.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleRemoveUser(user?.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Role Permissions Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Role Permissions</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><strong>Administrator:</strong> Full system access, manage users, configure settings</li>
          <li><strong>Accountant:</strong> Create/edit transactions, generate reports, reconcile accounts</li>
          <li><strong>Bookkeeper:</strong> Data entry, expense submission, basic reporting</li>
          <li><strong>Viewer:</strong> Read-only access to reports and transactions</li>
        </ul>
      </div>
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Invite Team Member</h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <Input
                  label="Email Address"
                  type="email"
                  value={inviteData?.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e?.target?.value })}
                  placeholder="user@example.com"
                  required
                />

                <Select
                  label="Role"
                  value={inviteData?.role}
                  onChange={(value) => setInviteData({ ...inviteData, role: value })}
                  options={roleOptions}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  onClick={handleInvite}
                  disabled={!inviteData?.email}
                >
                  Send Invitation
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPermissions;